import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { PageStatCards } from "@/components/PageStatCards";
import { TableToolbar } from "@/components/TableToolbar";
import { RowActions } from "@/components/RowActions";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { QtyInput } from "@/components/StockQty";
import { CompanyField } from "@/components/CompanyField";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtMoney } from "@/lib/format";

const EMPTY_ITEM = { item_id: "", quantity: "", unit_cost: "" };

export default function Receipts() {
  const { can } = usePermissions();
  const canCreateReceipt = can("STORES", "create");
  const { companies } = useCompany() || {};
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const [itemRes, whRes, ledgerRes] = await Promise.all([
        api.get("/inventory/items"), api.get("/inventory/warehouses"), api.get("/inventory/stock-ledger"),
      ]);
      setItems(itemRes.data);
      setWarehouses(whRes.data);
      setHistory(ledgerRes.data.filter((l) => l.movement_type === "RECEIPT"));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  useEffect(() => { load(); }, []);

  const itemLabel = (id) => { const i = items.find((it) => it.id === id); return i ? `${i.code} — ${i.name}` : id; };
  const quantityLabel = (quantity) => Number(quantity || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
  const warehouseName = (id) => warehouses.find((w) => w.id === id)?.name || id;

  const shown = useMemo(() => {
    if (!search) return history;
    const q = search.toLowerCase();
    return history.filter((h) =>
      itemLabel(h.item_id).toLowerCase().includes(q) ||
      warehouseName(h.warehouse_id).toLowerCase().includes(q)
    );
  }, [history, search, items, warehouses]);

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <PageHeader title="Receipts" description="Manual stock receipts — opening stock or non-PO material. GRNs raise stock automatically on posting." action={canCreateReceipt && <CreateReceiptDialog companies={companies} items={items} warehouses={warehouses} onCreated={load} />} />
      <PageStatCards stats={[
        { label: "Total Receipts", value: history.length, color: "blue" },
        { label: "Unique Items",   value: new Set(history.map(h => h.item_id)).size, color: "orange" },
        { label: "Warehouses",     value: new Set(history.map(h => h.warehouse_id)).size, color: "green" },
        { label: "Showing",        value: shown.length, color: "gray" },
      ]} />
      <TableToolbar
        search={search}
        onSearch={setSearch}
        csvData={shown.map(h => ({ Item: itemLabel(h.item_id), Warehouse: warehouseName(h.warehouse_id), Qty: quantityLabel(h.quantity), Cost: h.unit_cost }))}
        csvFilename="receipts.csv"
      />
      <div className="rounded-xl border border-border bg-card">
        {shown.length === 0 ? (
          <EmptyState title="No manual receipts yet" testId="receipts-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item / Warehouse</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((h) => (
                <TableRow key={h.id} data-testid={`receipt-row-${h.id}`}>
                  <TableCell className="text-foreground">{itemLabel(h.item_id)} · {warehouseName(h.warehouse_id)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">+{quantityLabel(h.quantity)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{fmtMoney(h.unit_cost)}</TableCell>
                  <TableCell className="text-right">
                    <RowActions testId={`receipt-actions-${h.id}`} onView={() => setSelected(h)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? itemLabel(selected.item_id) : ""}
        subtitle={`Manual Receipt`}
      >
        <DrawerSection label="Receipt Details">
          <DrawerField label="Item" value={selected ? itemLabel(selected.item_id) : null} />
          <DrawerField label="Warehouse" value={selected ? warehouseName(selected.warehouse_id) : null} />
          <DrawerField label="Quantity" value={selected?.quantity} mono />
          <DrawerField label="Unit Cost" value={selected?.unit_cost} mono />
          <DrawerField label="Movement Type" value={selected?.movement_type} />
          <DrawerField label="Reference" value={selected?.reference} />
        </DrawerSection>
      </DetailDrawer>
    </div>
  );
}

function CreateReceiptDialog({ companies, items, warehouses, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", warehouse_id: "", receipt_date: "" });
  const [lines, setLines] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateLine = (idx, field, value) => setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory/receipts", { ...form, items: lines });
      toast.success("Stock received");
      setForm({ company_id: "", warehouse_id: "", receipt_date: "" });
      setLines([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-receipt-button"><Plus className="h-4 w-4" /> New Receipt</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Stock Receipt</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-receipt-form">
          <div className="grid grid-cols-3 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="receipt-company-select" />
            <div>
              <Label>Warehouse</Label>
              <Select required value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger data-testid="receipt-warehouse-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="receipt-date-input" value={form.receipt_date} onChange={set("receipt_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {lines.map((l, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`receipt-item-row-${idx}`}>
                  <Select required value={l.item_id} onValueChange={(v) => updateLine(idx, "item_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`receipt-item-select-${idx}`}><SelectValue placeholder="Item" /></SelectTrigger>
                    <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <QtyInput required placeholder="Qty" className="w-24" unit={items.find((it) => it.id === l.item_id)?.uom_code || ""} value={l.quantity} onChange={(v) => updateLine(idx, "quantity", v)} data-testid={`receipt-item-qty-input-${idx}`} />
                  <Input required type="number" step="0.01" placeholder="Unit cost" className="w-28" value={l.unit_cost} onChange={(e) => updateLine(idx, "unit_cost", e.target.value)} data-testid={`receipt-item-cost-input-${idx}`} />
                  {lines.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setLines(lines.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setLines([...lines, { ...EMPTY_ITEM }])} data-testid="add-receipt-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-receipt-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Receive</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
